export const getCustomIngredientLabel = (item, index) => {
  const title = item?.title ?? item?.product?.title
  if (typeof title === 'string' && title.trim()) {
    return title.trim()
  }
  return `custom ingredient ${index + 1}`
}

const formatFailureReason = reason => {
  switch (reason) {
    case 'missing_product_reference':
      return 'missing product reference'
    case 'custom_product_save_failed':
      return 'could not be saved'
    case 'missing_title':
      return 'title is required'
    case 'not_persisted':
      return 'was not saved'
    default:
      return reason || 'failed to save'
  }
}

export const extractCustomIngredientErrorPayload = error =>
  error?.response?.data || error?.data || error?.details || null

export const isCustomIngredientSaveError = error => {
  if (error?.name === 'CustomIngredientSaveClientError') {
    return true
  }

  const payload = extractCustomIngredientErrorPayload(error)
  if (!payload) {
    return false
  }

  if (Array.isArray(payload.failures) && payload.failures.length > 0) {
    return true
  }

  if (payload.customIngredientsComplete === false) {
    return true
  }

  const requested = payload.requestedCustomIngredientCount
  const saved = payload.savedCustomIngredientCount
  return (
    requested != null && saved != null && Number(requested) !== Number(saved)
  )
}

export class CustomIngredientSaveClientError extends Error {
  constructor(message, details = {}) {
    super(message)
    this.name = 'CustomIngredientSaveClientError'
    this.details = details
  }
}

export const assertCustomIngredientSaveResponse = customIngredientSave => {
  if (!customIngredientSave) {
    return
  }

  if (customIngredientSave.customIngredientsComplete !== false) {
    return
  }

  throw new CustomIngredientSaveClientError(
    `Custom ingredient save incomplete: saved ${customIngredientSave.savedCustomIngredientCount} of ${customIngredientSave.requestedCustomIngredientCount}.`,
    customIngredientSave,
  )
}

export const validateCustomProductsForSave = (customProducts = []) => {
  const failures = []

  customProducts.forEach((item, index) => {
    const title = item?.title ?? item?.product?.title
    if (!title || !String(title).trim()) {
      failures.push({
        index,
        title: getCustomIngredientLabel(item, index),
        reason: 'missing_title',
      })
    }
  })

  return {
    valid: failures.length === 0,
    failures,
  }
}

export const formatCustomIngredientSaveErrorMessage = error => {
  const payload = extractCustomIngredientErrorPayload(error)

  if (payload?.message) {
    return payload.message
  }

  if (error?.message) {
    return error.message
  }

  return 'Failed to save custom ingredients'
}

export const formatCustomIngredientFailureDescription = error => {
  const payload = extractCustomIngredientErrorPayload(error)
  const failures = payload?.failures || error?.details?.failures || []

  if (failures.length > 0) {
    return failures
      .map(
        failure =>
          `${
            failure.title || `Ingredient ${failure.index + 1}`
          }: ${formatFailureReason(failure.reason)}`,
      )
      .join('. ')
  }

  if (
    payload?.requestedCustomIngredientCount != null &&
    payload?.savedCustomIngredientCount != null
  ) {
    return `Saved ${payload.savedCustomIngredientCount} of ${payload.requestedCustomIngredientCount} custom ingredients. Your recipe was not fully saved — please review the custom ingredients and try again.`
  }

  return 'One or more custom ingredients could not be saved. Please review them and try again.'
}

export const logCustomIngredientSaveFailure = (scope, error) => {
  const payload = extractCustomIngredientErrorPayload(error)
  console.error(`[menu-builder:${scope}] custom ingredient save failed`, {
    message: formatCustomIngredientSaveErrorMessage(error),
    payload,
    failures: payload?.failures || error?.details?.failures || [],
  })
}

export const notifyCustomIngredientSaveError = error => {
  const message = formatCustomIngredientSaveErrorMessage(error)
  const description = formatCustomIngredientFailureDescription(error)

  return {
    message,
    description,
  }
}
